import { NewProfileSidebar_userAccount$key } from '#app/__generated__/relay/NewProfileSidebar_userAccount.graphql.js';
import {
  NewProfileSidebar_userProfile$data,
  type NewProfileSidebar_userProfile$key,
} from '#app/__generated__/relay/NewProfileSidebar_userProfile.graphql.js';
import { NAVBAR_HEIGHT } from '#app/components/AppBar/constants.js';
import { Badges } from '#app/components/Badges/Badges.js';
import { IncompleteProfileBadge } from '#app/components/IncompleteProfileBadge/IncompleteProfileBadge.js';
import {
  FADE_IN_ANIMATION,
  FADE_OUT_ANIMATION,
} from '#app/components/Motion/constants.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useBadges } from '#app/hooks/useBadges.js';
import { useOnElementViewed } from '#app/hooks/useOnElementViewed.js';
import { useSelectedUserType } from '#app/hooks/useSelectedUserType.js';
import { BioV2 } from './BioV2/BioV2.js';
import { FullName } from './FullName.js';
import { HiringAsAnIndividual } from './HiringAsAnIndividual.js';
import { InquireAndSave } from './InquireAndSave.js';
import { Languages } from './Languages/Languages.js';
import { Links } from './Links.js';
import { Location } from './Location.js';
import { Organization } from './Organization.js';
import { PortfolioAndRecommendationsDataWrapper } from './PortfolioAndRecommendations/PortfolioAndRecommendationsDataWrapper.js';
import { ProfileSidebarAvatarV2 } from './ProfileSidebarAvatarV2.js';
import * as ProfileSidebarLayout from './ProfileSidebarLayout.js';
import { QuickResponderBadge } from './QuickResponderBadge.js';
import { Rate } from './Rate/Rate.js';
import { SkillsAndTools } from './SkillsAndTools/SkillsAndTools.js';
import { Timezone } from './Timezone/Timezone.js';
import { useStickyDesktopSidebar } from './useStickyDesktopSidebar.js';
import { graphql, useFragment } from '@contra/relay';
import { Suspense } from 'react';
import { css, styled } from 'styled-components';

const SidebarContainer = styled.div<{
  $fadeIn: boolean;
  $fadeOut: boolean;
  $showAbbreviatedSidebar: boolean;
}>`
  ${({ $fadeIn, $fadeOut, $showAbbreviatedSidebar }) => css`
    ${$showAbbreviatedSidebar
      ? css`
          position: sticky;

          // the 24px extra accounts for margin in the main layout
          top: calc(${NAVBAR_HEIGHT}px + 24px);
        `
      : undefined}

    ${$fadeIn
      ? css`
          animation: ${FADE_IN_ANIMATION};
        `
      : undefined}

    ${$fadeOut
      ? css`
          animation: ${FADE_OUT_ANIMATION};
        `
      : undefined}
  `}
`;

const ExtendedSidebarContainer = styled.div<{
  $isDisplayNone: boolean;
}>`
  ${({ $isDisplayNone }) => css`
    margin-top: 24px;

    ${$isDisplayNone
      ? css`
          display: none;
        `
      : undefined}
  `}
`;

const BadgesContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
`;

type NewProfileSidebarProps = {
  readonly openLinksInNewWindow?: boolean;
  readonly overriddenTags?: ReadonlyArray<{
    readonly id: string;
    readonly kind: 'skill' | 'tool';
    readonly logoImage?: null | { readonly uid: string };
    readonly name: string;
  }>;
  readonly showPortfolioAndRecommendationsSection?: boolean;
  readonly userAccountRef?: NewProfileSidebar_userAccount$key | null;
  readonly userProfileRef: NewProfileSidebar_userProfile$key;
};

const useSidebarContentVisibility = ({
  profile,
}: {
  profile: NewProfileSidebar_userProfile$data;
}) => {
  const showLinksSection =
    profile.visitorCanEdit || profile.externalLinks.count > 0;

  const showOrganizationSection =
    Boolean(profile.organization?.id) && !profile.isRemovedFromClientWorkspace;

  const showHiringAsAnIndividualSection = Boolean(
    profile.organization === null && profile.hasCompletedClientOnboarding,
  );

  const showSkillsAndToolsSection =
    profile.hasCompletedIndyOnboarding &&
    (profile.visitorCanEdit || profile.tags.length > 0);

  const showRateSection =
    profile.hasCompletedIndyOnboarding &&
    (profile.visitorCanEdit ||
      Boolean(profile.workPreferences.minimumHourlyRate));

  const showAboutSection =
    profile.visitorCanEdit ||
    profile.bio ||
    profile.location ||
    profile.workPreferences.languages.length > 0 ||
    (profile.workPreferences.timezonePrimaryUtcOffset !== null &&
      profile.workPreferences.timezoneOffsetTolerance !== null);

  return {
    showAboutSection,
    showHiringAsAnIndividualSection,
    showLinksSection,
    showOrganizationSection,
    showRateSection,
    showSkillsAndToolsSection,
  };
};

export const NewProfileSidebar = ({
  openLinksInNewWindow,
  overriddenTags,
  showPortfolioAndRecommendationsSection = true,
  userAccountRef,
  userProfileRef,
}: NewProfileSidebarProps) => {
  const { track } = useAnalytics();

  const userAccount = useFragment(
    graphql`
      fragment NewProfileSidebar_userAccount on UserAccount {
        ...Location_userAccount
      }
    `,
    userAccountRef,
  );

  const profile = useFragment<NewProfileSidebar_userProfile$key>(
    graphql`
      fragment NewProfileSidebar_userProfile on UserProfile
      @argumentDefinitions(
        showPortfolioAndRecommendationsSection: {
          type: "Boolean"
          defaultValue: true
        }
      ) {
        __typename
        id
        visitorCanEdit
        analyticsId
        hasCompletedClientOnboarding
        isRemovedFromClientWorkspace
        hasCompletedIndyOnboarding
        hasActivePortfolioSubscription
        hasPublishedPortfolio
        isQuickResponder

        visibleUserReviewSummary {
          count
        }

        externalLinks {
          count
        }

        organization {
          id
        }

        tags(
          tagTypes: [ROLE_TAG, TOOL_TAG]
          tagContexts: [WORK_PREFERENCE, PROFILE]
        ) {
          __typename
        }

        bio
        location
        workPreferences {
          minimumHourlyRate
          timezonePrimaryUtcOffset
          timezoneOffsetTolerance
          languages {
            __typename
          }
        }

        ...IncompleteProfileBadge_profile
        ...FullName_name
        ...InquireAndSave_userProfile
        ...BioV2_userProfile
        ...Location_userProfile
        ...ProfileSidebarAvatarV2_userProfile
        ...Links_userProfile
        ...Organization_userProfile
        ...HiringAsAnIndividual_userProfile
        ...Rate_userProfile
        ...Languages_userProfile
        ...SkillsAndTools_userProfile
        ...Timezone_userProfile
        ...useBadges_userProfile
        ...PortfolioAndRecommendationsDataWrapper_userProfile
          @include(if: $showPortfolioAndRecommendationsSection)
      }
    `,
    userProfileRef,
  );

  const onViewedRef = useOnElementViewed<HTMLDivElement>(() => {
    if (profile) {
      track('link', {
        action: 'viewed',
        is_owner: profile.visitorCanEdit,
        link_name: 'user_profile_external_link',
        owner_id: profile.analyticsId,
        section: 'sidebar',
      });
    }
  });
  const {
    showAboutSection,
    showHiringAsAnIndividualSection,
    showLinksSection,
    showOrganizationSection,
    showRateSection,
    showSkillsAndToolsSection,
  } = useSidebarContentVisibility({
    profile,
  });

  const {
    badges,
    hasBadges,
    memberSinceLabel,
    memberSinceTooltip,
    platformEarningsAmountLabel,
  } = useBadges({
    userProfileRef: profile,
  });

  const { fadeIn, fadeOut, showAbbreviatedSidebar, sidebarRef } =
    useStickyDesktopSidebar();

  const hasPortfolioOrRecommendations =
    (profile.hasActivePortfolioSubscription && profile.hasPublishedPortfolio) ||
    Boolean(profile.visibleUserReviewSummary);

  const { isClient } = useSelectedUserType();

  // show the add contact button when the profile is an indy with completed onboarding, and the visitor is an authenticated client
  const showAddContactButton =
    profile.hasCompletedIndyOnboarding && isClient && !profile.visitorCanEdit;

  return (
    <SidebarContainer
      $fadeIn={fadeIn}
      $fadeOut={fadeOut}
      $showAbbreviatedSidebar={showAbbreviatedSidebar}
      ref={sidebarRef}
    >
      <ProfileSidebarLayout.SidebarCard aria-label="Profile Sidebar">
        <ProfileSidebarLayout.SectionV2
          alignment="center"
          noMarginBottom={
            !hasPortfolioOrRecommendations &&
            !profile.visitorCanEdit &&
            showAbbreviatedSidebar
          }
        >
          <ProfileSidebarLayout.SectionContent alignment="center">
            <IncompleteProfileBadge userProfileRef={profile} />

            <ProfileSidebarAvatarV2
              hideMobileShareButton={showAddContactButton}
              openLinkInNewWindow={openLinksInNewWindow}
              userProfileRef={profile}
            />

            <FullName
              openLinkInNewWindow={openLinksInNewWindow}
              userProfileRef={profile}
            />

            <InquireAndSave
              showAddContactButton={showAddContactButton}
              userProfileRef={profile}
            />

            <QuickResponderBadge isQuickResponder={profile.isQuickResponder} />
          </ProfileSidebarLayout.SectionContent>
        </ProfileSidebarLayout.SectionV2>

        {showPortfolioAndRecommendationsSection && (
          <PortfolioAndRecommendationsDataWrapper accountRef={profile} />
        )}

        <ExtendedSidebarContainer $isDisplayNone={showAbbreviatedSidebar}>
          {showRateSection ? (
            <ProfileSidebarLayout.SectionV2>
              <ProfileSidebarLayout.SectionTitle>
                Rate
              </ProfileSidebarLayout.SectionTitle>
              <ProfileSidebarLayout.SectionContent>
                <Rate userProfileRef={profile} />
              </ProfileSidebarLayout.SectionContent>
            </ProfileSidebarLayout.SectionV2>
          ) : null}

          {(showSkillsAndToolsSection || Boolean(overriddenTags)) && (
            <ProfileSidebarLayout.SectionV2>
              <ProfileSidebarLayout.SectionTitle>
                Skills and Tools
              </ProfileSidebarLayout.SectionTitle>
              <ProfileSidebarLayout.SectionContent>
                <SkillsAndTools
                  overriddenTags={overriddenTags}
                  userProfileRef={profile}
                />
              </ProfileSidebarLayout.SectionContent>
            </ProfileSidebarLayout.SectionV2>
          )}

          {hasBadges ? (
            <ProfileSidebarLayout.SectionV2>
              <ProfileSidebarLayout.SectionTitle>
                Badges
              </ProfileSidebarLayout.SectionTitle>
              <ProfileSidebarLayout.SectionContent>
                <BadgesContainer>
                  <Badges
                    badges={badges}
                    memberSinceLabel={memberSinceLabel}
                    memberSinceTooltip={memberSinceTooltip}
                    platformEarningsAmountLabel={platformEarningsAmountLabel}
                    showFullLabel
                  />
                </BadgesContainer>
              </ProfileSidebarLayout.SectionContent>
            </ProfileSidebarLayout.SectionV2>
          ) : null}

          {showAboutSection ? (
            <ProfileSidebarLayout.SectionV2>
              <ProfileSidebarLayout.SectionTitle>
                About
              </ProfileSidebarLayout.SectionTitle>
              <ProfileSidebarLayout.SectionContent>
                <BioV2 userProfileRef={profile} />
                <Suspense>
                  <Location
                    userAccountRef={userAccount}
                    userProfileRef={profile}
                  />
                </Suspense>
                <Timezone userProfileRef={profile} />
                <Languages userProfileRef={profile} />
              </ProfileSidebarLayout.SectionContent>
            </ProfileSidebarLayout.SectionV2>
          ) : null}

          {showOrganizationSection ? (
            <ProfileSidebarLayout.SectionV2>
              <ProfileSidebarLayout.SectionContent>
                <Organization
                  openLinkInNewWindow={openLinksInNewWindow}
                  userProfileRef={profile}
                />
              </ProfileSidebarLayout.SectionContent>
            </ProfileSidebarLayout.SectionV2>
          ) : showHiringAsAnIndividualSection ? (
            <ProfileSidebarLayout.SectionV2>
              <ProfileSidebarLayout.SectionContent>
                <HiringAsAnIndividual userProfileRef={profile} />
              </ProfileSidebarLayout.SectionContent>
            </ProfileSidebarLayout.SectionV2>
          ) : null}

          {showLinksSection ? (
            <ProfileSidebarLayout.SectionV2 ref={onViewedRef}>
              <ProfileSidebarLayout.SectionTitle>
                Links
              </ProfileSidebarLayout.SectionTitle>
              <ProfileSidebarLayout.SectionContent>
                {/* key is needed here to ensure re-mount when changing between profiles */}
                <Links
                  key={profile.id}
                  userProfileRef={profile}
                />
              </ProfileSidebarLayout.SectionContent>
            </ProfileSidebarLayout.SectionV2>
          ) : null}
        </ExtendedSidebarContainer>
      </ProfileSidebarLayout.SidebarCard>
    </SidebarContainer>
  );
};
